<template>
  <VPopupWidget
    :name="computedTitle"
    :icon="$vuetify.icons.values.inviteUser"
    :visible="menu"
    :set-expand="
      (v) => {
        menu = v
      }
    "
    btnDepressed
    btnColor="accent"
    bottom
    left
  >
    <template slot="title">{{ $t('title') }}</template>

    <template slot="content" v-if="invitationSent">
      <v-alert color="success" :value="true" class="subheading bordered">{{ $t('successTitle') }}</v-alert>
    </template>

    <template slot="content" v-else>
      <FormErrors :show="showResourceErrors" :errors="resourceErrors" />
      <v-combobox
        dense
        v-model="form.user"
        :items="userList"
        item-text="display"
        item-value="display"
        :return-object="false"
        :hide-no-data="!search || search.length < 3"
        :search-input.sync="search"
        :label="$t('user')"
        persistent-hint
        :hint="$t('userHint')"
        required
        :error-messages="userErrors"
        @input="$v.form.user.$touch()"
        @blur="$v.form.user.$touch()"
      >
        <template slot="no-data">
          <v-list-item>
            <v-list-item-content class="text-body-2" v-html="$t('noData')" />
          </v-list-item>
        </template>

        <template slot="item" slot-scope="{ item }">
          <v-list-item-avatar v-if="item.avatar" size="32px">
            <img :src="item.avatar" />
          </v-list-item-avatar>
          <v-list-item-action v-else>
            <v-icon light v-text="$vuetify.icons.values.user" class="fa-fw"></v-icon>
          </v-list-item-action>
          <v-list-item-content>
            <v-list-item-title>{{ item.display }}</v-list-item-title>
          </v-list-item-content>
        </template>
      </v-combobox>
    </template>

    <template slot="footer-actions">
      <v-btn depressed v-if="!invitationSent" @click="submit" :loading="loading" :disabled="loading">
        {{ $t('sendInvitation') }}
      </v-btn>
      <v-btn depressed v-if="invitationSent" @click="invitationSent = false">
        {{ $t('inviteMore') }}
      </v-btn>
    </template>
  </VPopupWidget>
</template>

<script>
import { required } from 'vuelidate/lib/validators'
import { mapActions, mapState } from 'vuex'
import { NAMESPACE as ORG_NAMESPACE } from '../organizations/store'
import { NAMESPACE as USER_NAMESPACE } from '../users/store'
import { VPopupWidget, BaseForm, FormErrors } from '@argon/app/components'
const DEFAULT = {
  user: null
}

export default {
  name: 'InviteUser',
  extends: BaseForm,
  components: {
    VPopupWidget,
    FormErrors
  },
  props: {
    onSubmit: Function,
    preload: { type: Boolean, default: false },
    isIconBtn: { type: Boolean, default: false }
  },
  validations: {
    form: {
      user: { required }
    }
  },
  data: () => ({
    menu: false,
    search: null,
    invitationSent: false,
    form: { ...DEFAULT },
    loading: false
  }),
  watch: {
    menu: function (v) {
      if (!v) {
        this.resetServerErrors()
        this.form = { ...DEFAULT }
        this.invitationSent = false
        this.$v.form.$reset()
      }
    }
  },
  computed: {
    ...mapState(USER_NAMESPACE, ['users']),
    ...mapState(ORG_NAMESPACE, { orgUsers: 'users' }),
    existingIds() {
      return this.orgUsers.map((user) => user.user_id)
    },
    validUsers() {
      return this.users.filter((user) => !this.existingIds.includes(user.id))
    },
    userList() {
      return this.search && this.search.length > 2 ? this.validUsers : []
    },
    userErrors() {
      const errors = []
      if (!this.$v.form.user.$dirty) return errors
      !this.$v.form.user.required && errors.push(this.$t('required'))
      this.getServerErrors('user_id', errors)
      this.getServerErrors('email', errors)
      return errors
    },
    computedTitle() {
      if (!this.isIconBtn) {
        return this.$t('title')
      } else {
        return null
      }
    }
  },
  methods: {
    ...mapActions(USER_NAMESPACE, ['listUsers']),
    setRole(role) {
      this.userRole = role
    },
    submit() {
      this.resetServerErrors()
      if (this.$v.form.$invalid) {
        return this.$v.form.$touch()
      }
      this.loading = true
      let payload = { attribution: this.form.attribution }
      let user = this.users.find((user) => user.display === this.form.user)
      if (user) {
        payload['user_id'] = user.id
      } else {
        payload['email'] = this.form.user
      }
      this.onSubmit(payload)
        .then((response) => {
          this.invitationSent = true
          this.form = { ...DEFAULT }
          this.$v.$reset()
        })
        .catch(this.setServerErrors)
        .finally(() => {
          this.loading = false
        })
    }
  },
  created() {
    if (this.preload) {
      this.listUsers({}).catch(() => {})
    }
  }
}
</script>

<style scoped></style>

<i18n>
{
  "en": {
    "attribution": "@:labels.attribution",
    "inviteMore": "Invite More",
    "noData": "Looks like that person does not yet have an account. <br/> Type their email to invite them.",
    "required": "@:labels.required",
    "role": "@:labels.role",
    "sendInvitation": "Send Invitation",
    "successTitle": "Invitation Sent",
    "title": "Invite User",
    "user": "@:labels.user",
    "userHint": "type a user name or email"
  },
  "fr": {
    "attribution": "@:labels.attribution",
    "inviteMore": "Inviter plus",
    "noData": "Il semble que cette personne n'aie pas encore de compte. <br/> Tapez son courriel pour l'inviter.",
    "required": "@:labels.required",
    "role": "@:labels.role",
    "sendInvitation": "Envoyer l'invitation",
    "successTitle": "Invitation envoyée",
    "title": "Inviter un utilisateur",
    "user": "@:labels.user",
    "userHint": "tapez un identifiant ou un courriel"
  }
}
</i18n>
